import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Box, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow,
  Switch, Chip, Button, IconButton, Tooltip, Alert, Collapse,
  Dialog, DialogTitle, DialogContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArticleIcon from "@mui/icons-material/Article";
import BuildIcon from "@mui/icons-material/Build";

import api from "../api";
import { MONO, tokens } from "../theme";
import { PageHeader } from "../components/ui";
import VllmModelModal from "../components/VllmModelModal";

const STATUS_COLOR = { running: tokens.ok, starting: tokens.signal, error: tokens.crit, stopped: tokens.slate };

export default function VllmModels() {
  const [rows, setRows] = useState(null);
  const [env, setEnv] = useState(null);
  const [modal, setModal] = useState(null);     // {model} | {create:true} | null
  const [logFor, setLogFor] = useState(null);   // model name whose log is open
  const [logText, setLogText] = useState("");
  const [showEnvLog, setShowEnvLog] = useState(false);

  const load = useCallback(() => {
    api.get("/api/vllm-models", { silent: true })
      .then((d) => { setRows(d.models); setEnv(d.env); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while anything is starting or the env is building.
  const busy = (rows || []).some((m) => m.status === "starting") || env?.status === "building";
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [busy, load]);

  // Poll the open log.
  const logRef = useRef(null);
  useEffect(() => {
    if (!logFor) return;
    const fetchLog = () => api.get(`/api/vllm-models/${logFor}/log`, { silent: true })
      .then((d) => setLogText(d.log || "")).catch(() => {});
    fetchLog();
    const t = setInterval(fetchLog, 2000);
    return () => clearInterval(t);
  }, [logFor]);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [logText]);

  const toggle = async (m, on) => {
    try {
      await api.post(`/api/vllm-models/${m.name}/${on ? "start" : "stop"}`, {});
      load();
    } catch { /* toasts */ }
  };
  const del = async (name) => {
    if (!window.confirm(`Delete vLLM model "${name}"?`)) return;
    try { await api.del(`/api/vllm-models/${name}`); toast.success("Deleted"); load(); } catch { /* toasts */ }
  };
  const buildEnv = async () => {
    try { setEnv(await api.post("/api/vllm-models/env/rebuild", {})); setShowEnvLog(true); } catch { /* toasts */ }
  };

  if (!rows) return null;

  return (
    <Box>
      <PageHeader
        eyebrow="Serve"
        title="vLLM models"
        subtitle="Run any model in vLLM on a GPU node and consume it over the OpenAI API (/v1/chat/completions). Start a model to load it onto a GPU."
        actions={<Button variant="contained" size="small" onClick={() => setModal({ create: true })}>New model</Button>}
      />

      {env && env.status !== "ready" && (
        <Alert severity={env.status === "error" ? "error" : env.status === "building" ? "info" : "warning"}
          icon={<BuildIcon fontSize="small" />} sx={{ mb: 2 }}
          action={env.status !== "building" && (
            <Button color="inherit" size="small" onClick={buildEnv}>Build environment</Button>
          )}>
          {env.status === "building"
            ? "Building the shared vLLM environment (vllm + CUDA torch)… this is large."
            : env.status === "error"
            ? "The shared vLLM environment failed to build."
            : "The shared vLLM environment isn't built yet — it builds automatically on the first model start (or build it now)."}
          {(env.status === "building" || env.log) && (
            <Box sx={{ mt: 0.5 }}>
              <Button size="small" color="inherit" onClick={() => setShowEnvLog((s) => !s)} sx={{ px: 0, minWidth: 0 }}>
                {showEnvLog ? "hide log" : "show log"}
              </Button>
              <Collapse in={showEnvLog}>
                <Box component="pre" sx={{ fontFamily: MONO, fontSize: ".7rem", mt: 0.5, maxHeight: 200,
                  overflow: "auto", background: "#0d1a2b", color: "#c3d2ea", borderRadius: 1, p: 1, whiteSpace: "pre-wrap" }}>
                  {env.log || "…"}</Box>
              </Collapse>
            </Box>
          )}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="center">GPUs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.name}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                      {m.source === "custom" && <Chip size="small" variant="outlined" label="custom" />}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: ".78rem", color: "text.secondary" }}>{m.model_id}</TableCell>
                  <TableCell align="center">{m.tensor_parallel_size}</TableCell>
                  <TableCell>
                    <Chip size="small" label={m.status}
                      sx={{ bgcolor: `${STATUS_COLOR[m.status] || tokens.slate}22`,
                            color: STATUS_COLOR[m.status] || tokens.slate, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Switch size="small" checked={m.enabled} onChange={(e) => toggle(m, e.target.checked)} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Startup log">
                      <IconButton size="small" onClick={() => { setLogText(""); setLogFor(m.name); }}>
                        <ArticleIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setModal({ model: m })}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    {m.source === "custom" && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => del(m.name)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {modal && (
        <VllmModelModal model={modal.model || null}
          onClose={(changed) => { setModal(null); if (changed) load(); }} />
      )}

      <Dialog open={!!logFor} onClose={() => setLogFor(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: MONO, fontSize: ".9rem" }}>{logFor} — startup log</DialogTitle>
        <DialogContent dividers>
          <Box ref={logRef} component="pre" sx={{ fontFamily: MONO, fontSize: ".72rem", m: 0, maxHeight: 420,
            overflow: "auto", whiteSpace: "pre-wrap" }}>{logText || "(no log yet)"}</Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
